import { useQuery } from "@tanstack/react-query";
import { Autocomplete, TextField, Grid2 as Grid } from "@mui/material";
import { Controller } from "react-hook-form";
import { diagnosticsApi, type DxTestProfileDto, type DxTestProfileDetailDto } from "@/api/diagnostics";
import { servicesApi } from "@/api/endpoints";
import { MasterListPage } from "./MasterListPage";
import { MasterFormPage } from "./MasterFormPage";
import { statusColumn, TextFieldCell, SelectCell, SwitchCell } from "./fields";

const BASE = "/diagnostics/profiles";
const LIST_KEY = ["dx-profiles"];

export function DiagnosticsProfilesPage() {
  const departments = useQuery({ queryKey: ["dx-departments"], queryFn: () => diagnosticsApi.listDepartments() });
  const deptName = new Map((departments.data ?? []).map(d => [d.id, `${d.code} — ${d.name}`]));

  return (
    <MasterListPage<DxTestProfileDto>
      breadcrumbs={["Diagnostics", "Masters"]}
      title="Test profiles"
      subtitle="Panels of tests ordered together."
      queryKey={LIST_KEY}
      listFn={(inc) => diagnosticsApi.listProfiles(undefined, inc)}
      deleteFn={diagnosticsApi.deleteProfile}
      basePath={BASE}
      newLabel="New profile"
      labelOf={(r) => r.name}
      deleteNote="It will be refused if the profile has ever been ordered."
      searchPlaceholder="Search by code, name, or department"
      searchText={(r) => `${r.code} ${r.name} ${deptName.get(r.departmentId) ?? ""}`}
      columns={[
        { field: "code", headerName: "Code", width: 130 },
        { field: "name", headerName: "Name", flex: 1, minWidth: 220 },
        {
          field: "departmentId", headerName: "Department", width: 200,
          renderCell: (p) => deptName.get(p.row.departmentId) ?? "—",
        },
        { field: "testCount", headerName: "Tests", width: 90, align: "right", headerAlign: "right" },
        statusColumn<DxTestProfileDto>(),
      ]}
    />
  );
}

interface Values {
  departmentId: string; code: string; name: string;
  labTestIds: string[]; serviceId: string; isActive: boolean;
}

export function ProfileFormPage() {
  const departments = useQuery({ queryKey: ["dx-departments"], queryFn: () => diagnosticsApi.listDepartments() });
  const tests = useQuery({ queryKey: ["dx-tests"], queryFn: () => diagnosticsApi.listTests() });
  const services = useQuery({ queryKey: ["services-all"], queryFn: () => servicesApi.list({ pageSize: 200 }) });

  const testRows = tests.data ?? [];

  return (
    <MasterFormPage<Values, DxTestProfileDetailDto>
      breadcrumbs={["Diagnostics", "Test profiles"]}
      noun="test profile"
      basePath={BASE}
      listQueryKey={LIST_KEY}
      detailQueryKey={(id) => ["dx-profile", id]}
      maxWidth={860}
      defaultValues={{ departmentId: "", code: "", name: "", labTestIds: [], serviceId: "", isActive: true }}
      getFn={diagnosticsApi.getProfile}
      toValues={(d) => ({
        departmentId: d.departmentId, code: d.code, name: d.name,
        labTestIds: d.labTestIds, serviceId: d.serviceId ?? "", isActive: d.isActive,
      })}
      createFn={(v) => diagnosticsApi.createProfile({
        departmentId: v.departmentId, code: v.code.trim(), name: v.name.trim(),
        labTestIds: v.labTestIds, serviceId: v.serviceId || null,
      })}
      updateFn={(id, v) => diagnosticsApi.updateProfile(id, {
        departmentId: v.departmentId, code: v.code.trim(), name: v.name.trim(),
        labTestIds: v.labTestIds, serviceId: v.serviceId || null, isActive: v.isActive,
      })}
    >
      {({ control }, isEdit) => (
        <>
          <SelectCell control={control} name="departmentId" label="Department" span={6} required
            options={(departments.data ?? []).map(d => ({ value: d.id, label: `${d.code} — ${d.name}` }))}
            emptyLabel="— select department —" />
          <SelectCell control={control} name="serviceId" label="Billing service (price/GST)" span={6}
            options={(services.data?.items ?? []).map(s => ({ value: s.id, label: `${s.code} — ${s.name}` }))} />
          <TextFieldCell control={control} name="code" label="Code" span={4} required />
          <TextFieldCell control={control} name="name" label="Name" span={8} required />

          <Grid size={{ xs: 12 }}>
            <Controller
              name="labTestIds"
              control={control}
              rules={{ validate: (v: string[]) => v.length > 0 || "Pick at least one test" }}
              render={({ field, fieldState }) => (
                <Autocomplete
                  multiple
                  options={testRows}
                  loading={tests.isLoading}
                  getOptionLabel={(o) => `${o.code} — ${o.name}`}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  value={testRows.filter(t => field.value.includes(t.id))}
                  onChange={(_, picked) => field.onChange(picked.map(p => p.id))}
                  renderInput={(params) => (
                    <TextField {...params} size="small" label="Member tests *"
                      error={!!fieldState.error} helperText={fieldState.error?.message} />
                  )}
                />
              )}
            />
          </Grid>

          {isEdit && <SwitchCell control={control} name="isActive" label="Active" />}
        </>
      )}
    </MasterFormPage>
  );
}
